import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * Reusable confirmation dialog component
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {function} props.onConfirm - Function to call when action is confirmed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message
 * @param {string} props.confirmLabel - Label for the confirm button
 * @param {string} props.cancelLabel - Label for the cancel button
 * @param {string} props.type - Type of confirmation (delete, warning, info)
 * @param {boolean} props.loading - Whether the confirmation action is processing
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  type = 'warning',
  loading = false
}) => {
  // Determine icon and color based on type
  const getIconAndColor = () => {
    switch (type) {
      case 'delete':
        return { 
          icon: <DeleteIcon fontSize="large" />, 
          color: 'error.main',
          buttonColor: 'error'
        };
      case 'warning':
        return { 
          icon: <WarningIcon fontSize="large" />, 
          color: 'warning.main',
          buttonColor: 'warning'
        };
      case 'info':
      default:
        return { 
          icon: <InfoIcon fontSize="large" />, 
          color: 'info.main',
          buttonColor: 'primary'
        };
    }
  };

  const { icon, color, buttonColor } = getIconAndColor();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="inherit" 
          disabled={loading}
        >
          {cancelLabel}
        </Button>
        <Button 
          onClick={onConfirm} 
          color={buttonColor}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? 'Processing...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
